/**
 * Store Locator
 * Parses a remote CSV of store locations and renders them on a Google Map.
 *
 * Expected CSV columns (case-insensitive, flexible naming):
 *   name, address, city, state, zip, country, phone, lat, lng, hours, website, type
 *
 * Column aliases supported:
 *   latitude/lat, longitude/lng/lon, postal_code/zip/zipcode,
 *   store_name/name/location, street/address
 */

class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.stores = [];
    this.filteredStores = [];
    this.markers = [];
    this.map = null;
    this.infoWindow = null;
    this.userLocation = null;
  }

  connectedCallback() {
    this.csvUrl = this.dataset.csvUrl;
    this.apiKey = this.dataset.apiKey;
    this.defaultLat = parseFloat(this.dataset.defaultLat) || 39.8283;
    this.defaultLng = parseFloat(this.dataset.defaultLng) || -98.5795;
    this.defaultZoom = parseInt(this.dataset.defaultZoom) || 4;
    this.unitSystem = this.dataset.units || 'miles';

    if (!this.csvUrl) {
      this.querySelector('.store-locator__list').innerHTML =
        '<p class="store-locator__empty">Please configure a CSV URL in the section settings.</p>';
      return;
    }

    this.searchInput = this.querySelector('[data-search-input]');
    this.searchBtn = this.querySelector('[data-search-btn]');
    this.geoBtn = this.querySelector('[data-geo-btn]');
    this.listEl = this.querySelector('[data-store-list]');
    this.countEl = this.querySelector('[data-store-count]');
    this.filterEl = this.querySelector('[data-type-filter]');

    this.searchBtn?.addEventListener('click', () => this.handleSearch());
    this.searchInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.handleSearch(); }
    });
    this.geoBtn?.addEventListener('click', () => this.geolocate());
    this.filterEl?.addEventListener('change', () => this.applyFilter());

    this.loadGoogleMaps().then(() => {
      this.fetchCSV();
    });
  }

  loadGoogleMaps() {
    return new Promise((resolve) => {
      if (window.google && window.google.maps) { resolve(); return; }
      const existing = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existing) { existing.addEventListener('load', resolve); return; }
      window.__storeLocatorMapsReady = resolve;
      const s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + this.apiKey + '&callback=__storeLocatorMapsReady&libraries=geometry';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    });
  }

  async fetchCSV() {
    try {
      const resp = await fetch(this.csvUrl);
      if (!resp.ok) throw new Error('Failed to fetch CSV: ' + resp.status);
      const text = await resp.text();
      this.stores = this.parseCSV(text);
      this.filteredStores = [...this.stores];
      this.populateTypeFilter();
      this.initMap();
      this.renderList();
    } catch (err) {
      console.error('Store Locator CSV error:', err);
      this.listEl.innerHTML = '<p class="store-locator__empty">Unable to load store locations. Please check the CSV URL.</p>';
    }
  }

  parseCSV(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];

    const rawHeaders = this.parseCSVLine(lines[0]);
    const headerMap = this.mapHeaders(rawHeaders);
    const stores = [];

    for (let i = 1; i < lines.length; i++) {
      const vals = this.parseCSVLine(lines[i]);
      if (vals.length < 2) continue;
      const store = {};
      store.name = vals[headerMap.name] || '';
      store.address = vals[headerMap.address] || '';
      store.city = vals[headerMap.city] || '';
      store.state = vals[headerMap.state] || '';
      store.zip = vals[headerMap.zip] || '';
      store.country = vals[headerMap.country] || '';
      store.phone = vals[headerMap.phone] || '';
      store.lat = parseFloat(vals[headerMap.lat]) || 0;
      store.lng = parseFloat(vals[headerMap.lng]) || 0;
      store.hours = vals[headerMap.hours] || '';
      store.website = vals[headerMap.website] || '';
      store.type = vals[headerMap.type] || '';

      if (store.lat && store.lng) {
        store.fullAddress = [store.address, store.city, store.state, store.zip].filter(Boolean).join(', ');
        stores.push(store);
      }
    }
    return stores;
  }

  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"'; i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { result.push(current.trim()); current = ''; }
        else { current += ch; }
      }
    }
    result.push(current.trim());
    return result;
  }

  mapHeaders(headers) {
    const map = { name: -1, address: -1, city: -1, state: -1, zip: -1, country: -1, phone: -1, lat: -1, lng: -1, hours: -1, website: -1, type: -1 };
    const aliases = {
      name: ['name', 'store_name', 'store', 'location', 'location_name', 'title'],
      address: ['address', 'street', 'street_address', 'address1', 'address_1'],
      city: ['city', 'town'],
      state: ['state', 'province', 'region', 'state_province'],
      zip: ['zip', 'zipcode', 'zip_code', 'postal_code', 'postal', 'postcode'],
      country: ['country', 'country_code'],
      phone: ['phone', 'telephone', 'phone_number', 'tel'],
      lat: ['lat', 'latitude'],
      lng: ['lng', 'lon', 'longitude', 'long'],
      hours: ['hours', 'store_hours', 'opening_hours', 'business_hours'],
      website: ['website', 'url', 'web', 'link', 'site'],
      type: ['type', 'category', 'store_type', 'location_type'],
    };

    headers.forEach((h, i) => {
      const normalized = h.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      for (const [key, names] of Object.entries(aliases)) {
        if (names.includes(normalized) && map[key] === -1) {
          map[key] = i;
        }
      }
    });

    // Fallback: if name not found, use first column
    if (map.name === -1) map.name = 0;
    return map;
  }

  populateTypeFilter() {
    if (!this.filterEl) return;
    const types = [...new Set(this.stores.map((s) => s.type).filter(Boolean))];
    if (types.length <= 1) {
      this.filterEl.closest('.store-locator__filter')?.remove();
      return;
    }
    types.sort();
    types.forEach((t) => {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      this.filterEl.appendChild(opt);
    });
  }

  initMap() {
    const mapEl = this.querySelector('[data-map]');
    if (!mapEl) return;

    this.map = new google.maps.Map(mapEl, {
      center: { lat: this.defaultLat, lng: this.defaultLng },
      zoom: this.defaultZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    this.infoWindow = new google.maps.InfoWindow();
    this.addMarkers(this.filteredStores);
  }

  addMarkers(stores) {
    // Clear existing
    this.markers.forEach((m) => m.setMap(null));
    this.markers = [];

    const bounds = new google.maps.LatLngBounds();

    stores.forEach((store, i) => {
      const pos = { lat: store.lat, lng: store.lng };
      const marker = new google.maps.Marker({
        position: pos,
        map: this.map,
        title: store.name,
      });

      marker.addListener('click', () => {
        this.infoWindow.setContent(this.buildInfoWindow(store));
        this.infoWindow.open(this.map, marker);
        this.highlightListItem(i);
      });

      this.markers.push(marker);
      bounds.extend(pos);
    });

    if (stores.length > 0 && !this.userLocation) {
      this.map.fitBounds(bounds);
      if (stores.length === 1) this.map.setZoom(14);
    }
  }

  buildInfoWindow(store) {
    let html = '<div style="max-width:280px;font-family:inherit;line-height:1.5;">';
    html += '<strong style="font-size:14px;">' + this.esc(store.name) + '</strong><br>';
    if (store.fullAddress) html += '<span style="font-size:13px;">' + this.esc(store.fullAddress) + '</span><br>';
    if (store.phone) html += '<a href="tel:' + this.esc(store.phone) + '" style="font-size:13px;">' + this.esc(store.phone) + '</a><br>';
    if (store.hours) html += '<span style="font-size:12px;opacity:0.7;">' + this.esc(store.hours) + '</span><br>';
    if (store.website) html += '<a href="' + this.esc(store.website) + '" target="_blank" rel="noopener" style="font-size:13px;">Visit website</a><br>';
    html += '<a href="https://www.google.com/maps/dir/?api=1&destination=' + store.lat + ',' + store.lng + '" target="_blank" rel="noopener" style="font-size:13px;font-weight:600;">Get directions</a>';
    html += '</div>';
    return html;
  }

  renderList() {
    if (!this.listEl) return;
    if (this.countEl) this.countEl.textContent = this.filteredStores.length + ' location' + (this.filteredStores.length !== 1 ? 's' : '');

    if (this.filteredStores.length === 0) {
      this.listEl.innerHTML = '<p class="store-locator__empty">No stores found matching your search.</p>';
      return;
    }

    let html = '';
    this.filteredStores.forEach((store, i) => {
      html += '<button class="store-locator__item" data-index="' + i + '" type="button">';
      html += '<div class="store-locator__item-name">' + this.esc(store.name) + '</div>';
      html += '<div class="store-locator__item-address">' + this.esc(store.fullAddress) + '</div>';
      if (store.phone) html += '<div class="store-locator__item-phone">' + this.esc(store.phone) + '</div>';
      if (store.hours) html += '<div class="store-locator__item-hours">' + this.esc(store.hours) + '</div>';
      if (store.distance != null) {
        const unit = this.unitSystem === 'km' ? 'km' : 'mi';
        html += '<div class="store-locator__item-distance">' + store.distance.toFixed(1) + ' ' + unit + ' away</div>';
      }
      if (store.type) html += '<span class="store-locator__item-type">' + this.esc(store.type) + '</span>';
      html += '</button>';
    });

    this.listEl.innerHTML = html;

    this.listEl.querySelectorAll('.store-locator__item').forEach((el) => {
      el.addEventListener('click', () => {
        const idx = parseInt(el.dataset.index);
        this.selectStore(idx);
      });
    });
  }

  selectStore(index) {
    const store = this.filteredStores[index];
    if (!store || !this.map) return;
    const pos = { lat: store.lat, lng: store.lng };
    this.map.panTo(pos);
    this.map.setZoom(14);
    if (this.markers[index]) {
      this.infoWindow.setContent(this.buildInfoWindow(store));
      this.infoWindow.open(this.map, this.markers[index]);
    }
    this.highlightListItem(index);
  }

  highlightListItem(index) {
    this.listEl.querySelectorAll('.store-locator__item').forEach((el) => el.classList.remove('is-active'));
    const active = this.listEl.querySelector('[data-index="' + index + '"]');
    if (active) {
      active.classList.add('is-active');
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  handleSearch() {
    const query = this.searchInput?.value.trim();
    if (!query) {
      this.filteredStores = [...this.stores];
      this.applyFilter();
      return;
    }

    // Try geocoding the search query for location-based search
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        this.userLocation = { lat: loc.lat(), lng: loc.lng() };
        this.sortByDistance(this.userLocation);
        this.map.panTo(this.userLocation);
        this.map.setZoom(10);
      } else {
        // Fallback: text search on store name/city/state
        const q = query.toLowerCase();
        this.filteredStores = this.stores.filter((s) =>
          s.name.toLowerCase().includes(q) ||
          s.city.toLowerCase().includes(q) ||
          s.state.toLowerCase().includes(q) ||
          s.zip.includes(q) ||
          s.address.toLowerCase().includes(q)
        );
      }
      this.applyFilter();
    });
  }

  geolocate() {
    if (!navigator.geolocation) return;
    this.geoBtn.disabled = true;
    this.geoBtn.textContent = 'Locating...';

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.sortByDistance(this.userLocation);
        this.map.panTo(this.userLocation);
        this.map.setZoom(10);
        this.applyFilter();
        this.geoBtn.disabled = false;
        this.geoBtn.textContent = 'Use my location';
      },
      () => {
        this.geoBtn.disabled = false;
        this.geoBtn.textContent = 'Use my location';
      }
    );
  }

  sortByDistance(origin) {
    const from = new google.maps.LatLng(origin.lat, origin.lng);
    const factor = this.unitSystem === 'km' ? 0.001 : 0.000621371;
    this.stores.forEach((s) => {
      const to = new google.maps.LatLng(s.lat, s.lng);
      s.distance = google.maps.geometry.spherical.computeDistanceBetween(from, to) * factor;
    });
    this.filteredStores = [...this.stores].sort((a, b) => a.distance - b.distance);
  }

  applyFilter() {
    const typeVal = this.filterEl?.value;
    if (typeVal) {
      const base = this.filteredStores.length ? this.filteredStores : this.stores;
      this.filteredStores = base.filter((s) => s.type === typeVal);
    }
    this.addMarkers(this.filteredStores);
    this.renderList();
  }

  esc(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }
}

customElements.define('store-locator', StoreLocator);
